// useAlignment — the one Alignment Score source (Compass Sector Fill spec, 18 Oct 2026 §3).
// Home, the Compass, the Mountain and ATLAS all read this hook, so the same
// sector shows the same number everywhere. It normalises KYRS tasks (leaf tasks
// only — a parent with subtasks never counts itself), TKNE project tasks
// (designs.basic_stages leaves, via sectorProgress.projectLeaves) and METIS
// agenda instances (recurring occurrences, each with its own date) into
// WorkItems for the pure engine in alignment.ts. A row's sector comes from its
// own column, else its project's (or the project group's), else its task list's.
// LINKED: an Aim ref, a project, a task directly under an Aim task, or a task
// whose title IS one of the sector's Aims.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useDataVersion } from '../live/screenContext';
import { SECTORS, type Sector } from '../domain/olmps';
import { loadOracleSpine, type OracleSpine } from './oracle-spine-data';
import { useCompassConfig } from './sectorConfig';
import { scoreBoard, type AlignmentBoard } from './alignment';
import { directionSteps } from './useOracleProjects';
import { projectLeaves } from './sectorProgress';
import { asSector, keyOf, workItemsOf, type AlignmentRaw as Raw } from './alignmentItems';

const EMPTY: Raw = { tasks: [], agenda: [], lists: {}, designs: {}, projects: [], spine: null };

// One load per (user, window, data version), shared by every screen that reads
// alignment (Home, Compass, Mountain, ATLAS, the orb widget) — same rows, same number.
const inflight = new Map<string, Promise<Raw>>();

function fetchRaw(userId: string, span: number, version: number): Promise<Raw> {
  const k = `${userId}:${span}:${version}`;
  const hit = version >= 0 ? inflight.get(k) : undefined;
  if (hit) return hit;
  const p = (async (): Promise<Raw> => {
    const since = new Date();
    since.setDate(since.getDate() - span);
    const sinceKey = keyOf(since);
    const [t, a, l, d, st, spine] = await Promise.all([
      // Open tasks (any date) + anything touched inside the longest window.
      supabase
        .from('tasks')
        .select('id,title,scheduled,done,unlisted,sector,list_id,design_id,aim_ref,parent_task_id,updated_at')
        .eq('user_id', userId)
        .or(`done.eq.false,updated_at.gte.${since.toISOString()}`)
        .limit(4000),
      supabase.from('agenda_items').select('id,date,time,completed,sector,task_id,design_id,aim_ref,text,updated_at').eq('user_id', userId).gte('date', sinceKey).limit(2000),
      supabase.from('task_lists').select('id,olmps_sector').eq('user_id', userId),
      supabase.from('designs').select('id,sector,design_group_id,is_active,basic_stages').eq('user_id', userId),
      supabase.from('user_settings').select('design_groups').eq('user_id', userId).maybeSingle(),
      loadOracleSpine(supabase, userId).catch(() => null),
    ]);
    const lists: Record<string, Sector> = {};
    for (const r of (l.data ?? []) as { id: string; olmps_sector: string | null }[]) {
      const s = asSector(r.olmps_sector);
      if (s) lists[r.id] = s;
    }
    const groupSector: Record<string, Sector> = {};
    for (const g of Array.isArray(st.data?.design_groups) ? (st.data!.design_groups as { id?: string; sector?: string | null }[]) : []) {
      const s = asSector(g?.sector);
      if (g?.id && s) groupSector[g.id] = s;
    }
    const designs: Record<string, Sector> = {};
    const projects: Raw['projects'] = [];
    for (const r of (d.data ?? []) as { id: string; sector: string | null; design_group_id: string | null; is_active: boolean | null; basic_stages: unknown }[]) {
      const s = asSector(r.sector) ?? (r.design_group_id ? groupSector[r.design_group_id] ?? null : null);
      if (!s) continue;
      designs[r.id] = s;
      if (r.is_active) projects.push({ id: r.id, sector: s, leaves: projectLeaves({ steps: directionSteps(r.basic_stages) }) });
    }
    return { tasks: (t.data ?? []) as Raw['tasks'], agenda: (a.data ?? []) as Raw['agenda'], lists, designs, projects, spine };
  })();
  inflight.clear(); // only the newest version is worth keeping
  if (version >= 0) inflight.set(k, p);
  return p;
}

export interface UseAlignment {
  board: AlignmentBoard;
  loading: boolean;
  refresh: () => void;
}

export function useAlignment(userId: string | undefined): UseAlignment {
  const { config } = useCompassConfig(userId);
  const game = config.game;
  const span = Math.max(game.alignmentWindows.endeavour, game.alignmentWindows.goal, game.alignmentWindows.aim);
  const [raw, setRaw] = useState<Raw>(EMPTY);
  const [loading, setLoading] = useState(true);

  const dataVersion = useDataVersion();
  const load = useCallback(
    async (force = false) => {
      if (!userId) return;
      setLoading(true);
      setRaw(await fetchRaw(userId, span, force ? -1 : dataVersion));
      setLoading(false);
    },
    [userId, span, dataVersion],
  );

  useEffect(() => {
    void load();
  }, [load, dataVersion]);

  const board = useMemo(() => {
    const hasEndeavour = Object.fromEntries(SECTORS.map((s) => [s, !!raw.spine?.[s]?.endeavour?.trim()])) as Record<Sector, boolean>;
    return scoreBoard(SECTORS, { items: workItemsOf(raw), hasEndeavour, now: new Date(), game });
  }, [raw, game]);

  return { board, loading, refresh: () => void load(true) };
}
